<template>
  <teleport to="body">
    <div v-if="isOpen" class="modal" @click.self.prevent="closeModal">
      <div class="wrapper">
        <font-awesome-icon icon="fa-xmark" class="exit" @click="closeModal" />
        <div class="content">
          <h3 class="title">Billing Information</h3>

          <div id="why-section" class="section">
            <h3>Why is this different from my invoice?</h3>
            <p>The price shown here reflects the monthly cost of your updated plan.</p>
            <p>Your invoice may differ because it includes proration — Stripe’s method of charging only for the portion of the billing cycle you actually used.</p>
            <p>Proration only applies when you make changes to your subscription during an active billing period.</p>
            <p>If no changes are made within a billing cycle, your invoice will match the price displayed here.</p>

            <p class="summary">In short, the invoice may vary because it’s automatically adjusted to the exact usage within your billing cycle.</p>
          </div>

          <div id="how-section" class="section">
            <h3>How is my invoice calculated?</h3>
            <p class="intro">Stripe takes your changes and breaks them into clean pieces:</p>

            <div class="calculation-steps">
              <div class="step">
                <h4>Looks at your old subscription</h4>
                <p>Calculates how much of the current billing period you haven't used yet</p>
                <p>Gives you a credit for that unused time</p>
              </div>

              <div class="step">
                <h4>Looks at your new subscription</h4>
                <p>Calculates how much of the current billing period is left</p>
                <p>Charges you a partial amount for that remaining time</p>
              </div>

              <div class="step">
                <h4>Adds or subtracts the difference</h4>
                <p>The result becomes your upcoming invoice total</p>
              </div>
            </div>

            <p class="summary">In short: you only pay for what you use, down to the fraction of the billing period.</p>
          </div>
          <div class="actions">
            <button class="action" @click="closeModal">Got it</button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: "ProrationInfo",
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    scrollTo: {
      type: String,
      default: null,
      validator: (value) => ["why", "how", null].includes(value),
    },
  },
  emits: ["close"],
  methods: {
    closeModal() {
      this.$emit("close");
    },
    scrollToSection() {
      if (this.scrollTo) {
        this.$nextTick(() => {
          const element = document.getElementById(this.scrollTo + "-section");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
    },
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.scrollToSection();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@monolith/styles";
.wrapper {
  background: var(--secondary-colour);
  max-width: 75%;

  .content {
    padding: 1rem;
    max-height: 60vh;
    overflow-y: auto;

    .section {
      margin-bottom: 2rem;

      h3 {
        color: var(--action-colour);
        margin: 1rem 0;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid var(--action-colour);
      }
      p {
        margin: 0.25rem 0;
      }
    }

    .intro {
      margin-bottom: 1.5rem;
      color: var(--text-colour);
      line-height: 1.6;
    }

    p {
      color: var(--text-colour);
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    .scenario {
      margin-bottom: 1rem;
      padding: 1rem;
      background-color: var(--primary-colour);
      border-radius: 0.5rem;
      box-shadow: var(--box-shadow);
    }

    .calculation-steps {
      margin: 1.5rem 0;

      .step {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: var(--primary-colour);
        border-radius: 0.5rem;
        box-shadow: var(--box-shadow);
        h4 {
          margin: 0.5rem 0;
        }
        p {
          margin: 0.5rem 1.5rem;
          color: var(--action-colour);
        }
      }
    }

    .summary {
      margin-top: 1.5rem;
      padding: 1rem;
      background-color: var(--primary-colour);
      border-radius: 0.5rem;
      box-shadow: var(--box-shadow);
    }

    .actions {
      .action {
        background-color: var(--primary-colour);
        color: var(--action-colour);
        border: none;
        border-radius: 0.5rem;
        padding: 1rem;
        box-shadow: var(--box-shadow);
        cursor: pointer;
      }
    }
  }
}
</style>
